import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { CampusFirstLevelPage, CampusRoutes, CampusSecondLevelPage, CampusThirdLevelPage } from './routes.js';

// Pages
import '../../../src/client/pages/chancen-page-impressum-privacy.js';
import './pages/campus-page-administration-children-statistics.js';
import './pages/campus-page-administration-course-presence.js';
import './pages/campus-page-administration-event-edit.js';
import './pages/campus-page-administration-group-edit.js';
import './pages/campus-page-administration-groups.js';
import './pages/campus-page-administration-leko-attendance.js';
import './pages/campus-page-administration-list-event.js';
import './pages/campus-page-administration-notifications.js';
import './pages/campus-page-administration-scopes.js';
import './pages/campus-page-administration-statistics.js';
import './pages/campus-page-administration-poll-management.js';
import './pages/campus-page-all-notifications.js';
import './pages/campus-page-blanko-accounts.js';
import './pages/campus-page-editorial-edit-lesson.js';
import './pages/campus-page-editorial-lounge-external.js';
import './pages/campus-page-editorial-lounge.js';
import './pages/campus-page-editorial.js';
import './pages/campus-page-export-learning-data.js';
import './pages/campus-page-landing.js';
import './pages/campus-page-lessons.js';
import './pages/campus-page-lessons-chancenkurve.js';
import './pages/campus-page-levumi-german-functions.js';
import './pages/campus-page-levumi-math-functions.js';
import './pages/campus-page-levumi-number-dictation-functions.js';
import './pages/campus-page-lounge-external.js';
import './pages/campus-page-lounge.js';
import './pages/campus-page-not-found.js';
import './pages/campus-page-play-lesson.js';
import './pages/campus-page-student-choose-digital-courses.js';
import './pages/campus-page-student-dashboard.js';
import './pages/campus-page-student-knowledge-answer-details.js';
import './pages/campus-page-student-knowledge-cumulative-diagnostik.js';
import './pages/campus-page-student-knowledge-diagnostik.js';
import './pages/campus-page-student-your-lessons.js';
import './pages/campus-page-teacher-dashboard-employee-list.js';
import './pages/campus-page-teacher-dashboard-hefte.js';
import './pages/campus-page-teacher-dashboard-student-list.js';
import './pages/campus-page-teacher-dashboard-student-profile.js';
import './pages/campus-page-work-tracking-overview.js';
import './pages/campus-page-work-tracking.js';
import './pages/exercise/campus-page-exercise-edit-chemistry-formular.js';
import './pages/exercise/campus-page-exercise-edit-geogebra.js';
import './pages/exercise/campus-page-exercise-edit-h5p.js';
import './pages/exercise/campus-page-exercise-edit-levumi.js';
import './pages/exercise/campus-page-exercise-edit-serlo-editor.js';
import './pages/exercise/campus-page-exercise-edit-serlo.js';
import './pages/exercise/campus-page-exercise-edit-text.js';

const mustBeAuthed = (render: () => any) => {
  return authenticationGuard(
    () => render(),
    html`<chancen-component-login-redirect></chancen-component-login-redirect>`,
  );
};

const mustHaveRole = (currentUser: Maybe<ChancenwerkUser>, role: Role, render: () => any) => {
  return mustBeAuthed(() => {
    if (!currentUser?.checkRole(role)) {
      return html`<campus-page-no-permission></campus-page-no-permission>`;
    }
    return render();
  });
};

export const campusRouter = makeRouter(
  {
    root: {
      path: '/',
      render: stateful(params => {
        const user = useObservable(() => currentUser<ChancenwerkUser>(), []);
        if (user) {
          setTimeout(() => goTo(CampusRoutes.dashboard), 100);
          return nothing;
        }
        return html` <campus-page-landing></campus-page-landing> `;
      }),
    },

    // Auth and Basic Pages
    [CampusFirstLevelPage.PRIVACY]: {
      path: `/${CampusFirstLevelPage.PRIVACY}`,
      render: () => html` <chancen-page-privacy></chancen-page-privacy> `,
    },
    [CampusFirstLevelPage.IMPRESS]: {
      path: `/${CampusFirstLevelPage.IMPRESS}`,
      render: () => html` <chancen-page-impressum></chancen-page-impressum> `,
    },

    // Work Tracking
    [CampusFirstLevelPage.WORK_TRACKING]: {
      path: `/${CampusFirstLevelPage.WORK_TRACKING}`,
      render: () => html` <campus-page-work-tracking-overview></campus-page-work-tracking-overview> `,
    },
    'work-tracking-detail': {
      path: `/${CampusFirstLevelPage.WORK_TRACKING}/:id`,
      render: ({ id }) => html` <campus-page-work-tracking .eid=${id}></campus-page-work-tracking> `,
    },

    // Teacher Dashboard
    [CampusFirstLevelPage.TEACHER_DASHBOARD]: {
      path: `/${CampusFirstLevelPage.TEACHER_DASHBOARD}`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-teacher-dashboard-student-list></campus-page-teacher-dashboard-student-list>`,
        ),
    },
    'teacher-dashboard-employee': {
      path: `/${CampusFirstLevelPage.TEACHER_DASHBOARD}/${CampusSecondLevelPage.TEACHER_DASHBOARD_EMPLOYEE}`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-teacher-dashboard-employee-list></campus-page-teacher-dashboard-employee-list>`,
        ),
    },
    'teacher-dashboard-hefte': {
      path: `/${CampusFirstLevelPage.TEACHER_DASHBOARD}/${CampusSecondLevelPage.HEFTE}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-teacher-dashboard-hefte></campus-page-teacher-dashboard-hefte>`),
    },
    'teacher-dashboard-student': {
      path: `/${CampusFirstLevelPage.TEACHER_DASHBOARD}/profile/:id`,
      render: ({ id }) =>
        mustBeAuthed(
          () =>
            html`<campus-page-teacher-dashboard-student-profile
              .userID=${id}
            ></campus-page-teacher-dashboard-student-profile>`,
        ),
    },

    // Admin Routes
    'admin-scopes': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.SCOPES}`,
      render: () => mustBeAuthed(() => html`<campus-page-administration-scopes></campus-page-administration-scopes>`),
    },
    'admin-groups': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.GROUPS}`,
      render: () => mustBeAuthed(() => html`<campus-page-administration-groups></campus-page-administration-groups>`),
    },
    'admin-group-new': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.GROUPS}/edit`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-administration-group-edit></campus-page-administration-group-edit>`),
    },
    'admin-group-edit': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.GROUPS}/edit/:id`,
      render: ({ id }) =>
        mustBeAuthed(
          () => html`<campus-page-administration-group-edit groupID=${id}></campus-page-administration-group-edit>`,
        ),
    },
    'admin-children-stats': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.CHILDREN_STATISTICS}`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-administration-children-statistics></campus-page-administration-children-statistics>`,
        ),
    },
    'admin-stats': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.STATISTICS}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-administration-statistics></campus-page-administration-statistics>`),
    },
    'admin-presence': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.PRESENCE}`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-administration-course-presence></campus-page-administration-course-presence>`,
        ),
    },
    'admin-notifications': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.NOTIFICATIONS}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-administration-notifications></campus-page-administration-notifications>`),
    },
    'admin-leko-absence': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.LEKO_ABSENCE}`,
      render: stateful(() => {
        const user = useObservable(() => currentUser<ChancenwerkUser>(), []);
        return mustHaveRole(
          user,
          Role.LEKO,
          () => html`<campus-page-administration-leko-attendance></campus-page-administration-leko-attendance>`,
        );
      }),
    },
    'admin-blanko': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.BLANKO_ACCOUNTS}`,
      render: () => mustBeAuthed(() => html`<campus-page-blanko-accounts></campus-page-blanko-accounts>`),
    },
    'admin-events': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.EVENTS}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-administration-list-event></campus-page-administration-list-event>`),
    },
    'admin-event-edit': {
      path: `/${CampusFirstLevelPage.ADMIN}/${CampusSecondLevelPage.EVENTS}/edit/:id`,
      render: ({ id }) =>
        mustBeAuthed(
          () => html`<campus-page-administration-event-edit eventID=${id}></campus-page-administration-event-edit>`,
        ),
    },
    'admin-poll-management': {
      path: `/${CampusFirstLevelPage.ADMIN}/poll-management`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-administration-poll-management></campus-page-administration-poll-management>`,
        ),
    },

    // Campus Routes
    'campus-student': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.DASHBOARD}`,
      render: () => mustBeAuthed(() => html`<campus-student-dashboard></campus-student-dashboard>`),
    },
    'campus-student-lessons': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.ALL_LESSONS}`,
      render: () => mustBeAuthed(() => html`<campus-page-lessons></campus-page-lessons>`),
    },
    'campus-student-lessons-chancenkurve': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.ALL_LESSONS_CHANCENKURVE}`,
      render: () => mustBeAuthed(() => html`<campus-page-lessons-chancenkurve></campus-page-lessons-chancenkurve>`),
    },
    'campus-student-your-lessons': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.YOUR_LESSONS}`,
      render: () => mustBeAuthed(() => html`<campus-page-student-your-lessons></campus-page-student-your-lessons>`),
    },
    'campus-student-play': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.PLAY}/:id`,
      render: ({ id }) => mustBeAuthed(() => html`<campus-page-play-lesson .lessonID=${id}></campus-page-play-lesson>`),
    },
    'campus-student-digital-courses': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.CHOOSE_DIGITAL_COURSES}`,
      render: () =>
        mustBeAuthed(() => html`<campus-student-choose-digital-courses></campus-student-choose-digital-courses>`),
    },
    'campus-student-notifications': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.STUDENT}/${CampusThirdLevelPage.ALL_NOTIFICATIONS}`,
      render: () => mustBeAuthed(() => html`<campus-page-all-notifications></campus-page-all-notifications>`),
    },

    // Editorial Routes
    'campus-editorial': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}`,
      render: () => mustBeAuthed(() => html`<campus-student-dashboard></campus-student-dashboard>`),
    },
    'campus-editorial-lessons': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LESSONS}`,
      render: () => mustBeAuthed(() => html`<campus-page-editorial></campus-page-editorial>`),
    },
    'campus-editorial-lesson-edit': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LESSONS}/edit/:id?`,
      render: ({ id }) =>
        mustBeAuthed(
          () => html`<campus-page-editorial-edit-lesson .lessonID=${id}></campus-page-editorial-edit-lesson>`,
        ),
    },
    'campus-editorial-exercise': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.EXCERCISES}/edit/:type`,
      render: ({ type }) => {
        const exerciseType = parseInt(type ?? '0');
        return mustBeAuthed(() => {
          switch (exerciseType) {
            case ExerciseType.FREITEXT:
              return html`<campus-page-exercise-edit-text></campus-page-exercise-edit-text>`;
            case ExerciseType.CHEMISTRY_FORMULA:
              return html`<campus-page-exercise-edit-chemistry-formular></campus-page-exercise-edit-chemistry-formular>`;
            case ExerciseType.GEOGEBRA:
              return html`<campus-page-exercise-edit-geogebra></campus-page-exercise-edit-geogebra>`;
            case ExerciseType.H5P:
              return html`<campus-page-exercise-edit-h5p></campus-page-exercise-edit-h5p>`;
            case ExerciseType.LEVUMIMATH:
              return html`<campus-page-exercise-edit-levumi-math></campus-page-exercise-edit-levumi-math>`;
            case ExerciseType.LEVUMIGERMAN:
              return html`<campus-page-exercise-edit-levumi-german></campus-page-exercise-edit-levumi-german>`;
            case ExerciseType.LEVUMI_NUMBER_DICTATE:
              return html`<campus-page-exercise-edit-levumi-number-dictate></campus-page-exercise-edit-levumi-number-dictate>`;
            case ExerciseType.SERLO:
              return html`<campus-page-exercise-edit-serlo></campus-page-exercise-edit-serlo>`;
            case ExerciseType.SERLO_EDITOR:
              return html`<campus-page-exercise-edit-serlo-editor></campus-page-exercise-edit-serlo-editor>`;
            default:
              return html`<campus-page-not-found></campus-page-not-found>`;
          }
        });
      },
    },
    'campus-editorial-lounge': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LOUNGE}`,
      render: () => mustBeAuthed(() => html`<campus-page-editorial-lounge></campus-page-editorial-lounge>`),
    },
    'campus-editorial-lounge-external': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LOUNGE_EXTERNAL}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-editorial-lounge-external></campus-page-editorial-lounge-external>`),
    },
    'campus-editorial-levumi-math': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LEVUMI_MATH}`,
      render: () => mustBeAuthed(() => html`<campus-page-levumi-math-functions></campus-page-levumi-math-functions>`),
    },
    'campus-editorial-levumi-german': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LEVUMI_GERMAN}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-levumi-german-functions></campus-page-levumi-german-functions>`),
    },
    'campus-editorial-levumi-number-dictation': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.LEVUMI_NUMBER_DICTATION}`,
      render: () =>
        mustBeAuthed(
          () => html`<campus-page-levumi-number-dictation-functions></campus-page-levumi-number-dictation-functions>`,
        ),
    },
    'campus-editorial-export': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.EXPORT}`,
      render: () => mustBeAuthed(() => html`<campus-page-export-learning-data></campus-page-export-learning-data>`),
    },
    'campus-editorial-diagnostics': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.DIAGNOSTICS}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-student-knowledge-diagnostik></campus-page-student-knowledge-diagnostik>`),
    },
    'campus-editorial-diagnostics-details': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.DIAGNOSTICS}/:id`,
      render: ({ id }) =>
        mustBeAuthed(
          () =>
            html`<campus-page-student-knowledge-diagnostik-details
              .selectedUserID=${id}
            ></campus-page-student-knowledge-diagnostik-details>`,
        ),
    },
    'campus-editorial-cumulative-diagnostics': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.CUMULATIVE_DIAGNOSTICS}`,
      render: () =>
        mustBeAuthed(
          () =>
            html`<campus-page-student-knowledge-cumulative-diagnostik></campus-page-student-knowledge-cumulative-diagnostik>`,
        ),
    },
    'campus-editorial-answers': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.ANSWERS}`,
      render: () =>
        mustBeAuthed(() => html`<campus-page-student-knowledge-diagnostik></campus-page-student-knowledge-diagnostik>`),
    },
    'campus-editorial-answers-details': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.EDITORIAL}/${CampusThirdLevelPage.ANSWERS}/:id`,
      render: ({ id }) =>
        mustBeAuthed(
          () =>
            html`<campus-page-student-knowledge-answer-details
              .selectedUserID=${id}
            ></campus-page-student-knowledge-answer-details>`,
        ),
    },

    // Lounge Routes
    'campus-lounge': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.LOUNGE}`,
      render: () => mustBeAuthed(() => html`<campus-page-lounge></campus-page-lounge>`),
    },
    'campus-lounge-external': {
      path: `/${CampusFirstLevelPage.CHANCENCAMPUS}/${CampusSecondLevelPage.LOUNGE_EXTERNAL}`,
      render: () => mustBeAuthed(() => html`<campus-page-lounge-external></campus-page-lounge-external>`),
    },
  },
  {
    fallback: () => {
      return html`<campus-page-no-permission></campus-page-no-permission>`;
    },
  },
).init();
